import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Observable, ReplaySubject, Subject, Subscription, first, timer } from 'rxjs';
import { LoadingScreenService } from './loading-screen.service';
const TEN_MINUTES_MILLIS = 600000;
const ONE_HOUR_MILLIS = 3.6e+6;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  user: any;
  token: string;
  authenticated: boolean;
  userPermissions$ = new Subject();

  inactivityTimer: Subscription;
  lastRefresh: number;
  refreshing = false;

  constructor(
    private router: Router,
    private location: Location,
    private loadingService: LoadingScreenService
  ) {
    // Used for listening to login events
    Hub.listen("auth", ({ payload: { event, message } }) => {
      if (event === "signedIn") {
        if (this.isAccessTokenPresent()) {
          fetchAuthSession().then(session => {
            this.setSession(session);
          }).catch(e => {
            console.error("Exception when getting current session in hub: ", e);
            signOut();
            signInWithRedirect();
          });
        }
      } else if (event === "signedOut") {
        this._logoutCleanup();
      } else if (event === "tokenRefresh") {
        console.log('auth tokens have been refreshed.');
      } else if (event === "signInWithRedirect") {
        console.log('Signed in with redirect');
      } else {
        console.log(`Error signing in: ${message}`, event);
      }
    });

    if (!this.location.path().startsWith('/login')) {
      //store variable to pull after redirect complete
      localStorage.setItem('redirectRoute', this.location.path());
    }

    window.addEventListener('storage', (event) => {
      if (event.key && event.key.split(".").pop() === 'accessToken' && event.newValue === null) {
        this.logout();
      }
    });

    this.startInactivityTimer();
  }


  setSession(session) {
    this.loadingService.startLoading();
    const idToken = session?.tokens?.idToken;
    this.token = idToken.toString();
    const user = {
      products: "",
      permissions: "",
      ...idToken.payload
    };
    user.initialized = user.products != "";
    user.products = user.products.split(',');
    user.permissions = user.permissions.split(',');

    this.user = user;
    this.userPermissions$.next(this.user.permissions);

    this.authenticated = true;

    this.lastRefresh = Date.now();
    this.loadingService.stopLoading();
    this.navigateToCachedPath();
  }

  navigateToCachedPath() {
    const pathObs = this.getPath();
    pathObs.pipe(first()).subscribe(path => {
      if (!path[1]) {
        // if parameter doesn't exist navigate by string only
        this.router.navigate(path, { replaceUrl: true });
      } else {
        // include parameter into the url string
        const param = path[1].split('=')[1];
        this.router.navigate([path[0]], { queryParams: { id: param } });
      }
    });
  }

  getPath(): Observable<string[]> {
    const redirectRoute = localStorage.getItem('redirectRoute');
    const localSubject = new ReplaySubject<string[]>();
    if (redirectRoute) {
      //return an array of a query string and its parameter
      localSubject.next(localStorage.getItem('redirectRoute').split('?'));
    } else {
      localSubject.next(["/home"]);
    }

    return localSubject.asObservable();
  }

  async refreshToken() {
    const now = Date.now();
    //  Only refresh once every 10 minutes
    if (now - this.lastRefresh > TEN_MINUTES_MILLIS) {
      this.refreshing = true;
      try {
        // https://docs.amplify.aws/gen1/angular/build-a-backend/auth/auth-migration-guide/#authcurrentsession
        const currentSession = await fetchAuthSession({ forceRefresh: true });
        this.token = currentSession?.tokens?.idToken.toString();
      } catch (e) {
        console.warn('Unable to refresh Token', e);
      } finally {
        this.lastRefresh = now;
      }
      this.refreshing = false;
    }
  }

  getUserPermissions(): Observable<any> {
    return this.userPermissions$.asObservable();
  }

  isAccessTokenPresent() {
    return localStorage.length !== 0 && Object.keys(localStorage).some(key => key.split(".").pop() === 'accessToken');
  }

  resetInactivityTimer() {
    // This is called from app.component.ts
    // It's bound to a global event listener for mousedown
    if (!this.refreshing) {
      this.inactivityTimer.unsubscribe();
      this.refreshToken();
      this.startInactivityTimer();
    }
  }

  startInactivityTimer() {
    // Set a timer for 1 hour of inactivity
    this.inactivityTimer = timer(ONE_HOUR_MILLIS).subscribe(this.logout);
  }

  login() {
    if (this.isAccessTokenPresent()) {
      fetchAuthSession().then(session => {
        this.setSession(session);
      }).catch(e => {
        console.error("Exception when getting current session in login: ", e);
        signOut();
        signInWithRedirect();
      });
    } else {
      signInWithRedirect(); // This is the call that would normally redirect a user to the signin page.
    }
  }

  async logout() {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  }

  _logoutCleanup() {
    this.user = undefined;
    this.authenticated = false;
    this.token = '';
    timer(500).pipe(first()).subscribe(() => this.router.navigate(['/login']));
  }

  get isLoggedIn(): boolean {
    return this.authenticated;
  }

  async getToken() {
    return (await fetchAuthSession()).tokens?.idToken;
  }

  get userProfile() {
    return this.user;
  }
}